import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { 
  LogOut, Save, Trash2, Download, ChevronDown, ChevronUp, 
  HelpCircle, Building2, Calculator, TrendingUp, PieChart 
} from 'lucide-react';

// Master passcode for regular users
const MASTER_CODE = 'seirai@2026';

// Admin account
const ADMIN_ACCOUNT = {
  email: 'office@seiraigroup.com',
  password: 'seiRai@2026'
};

// Helper function to format currency
const fmt = (number) => {
  if (number === null || number === undefined || isNaN(number) || !isFinite(number)) {
    return '¥0';
  }
  return `¥${Math.round(number).toLocaleString()}`;
};

// API base URL
const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

// API helpers
const saveToAPI = async (email, propertyName, formData) => {
  try {
    const response = await fetch(`${API_BASE}/api/simulator/save`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        user_email: email,
        property_name: propertyName,
        data: formData
      })
    });
    return response.ok;
  } catch (e) {
    console.error('Error saving to API:', e);
    return false;
  }
};

const loadFromAPI = async (email) => {
  try {
    const response = await fetch(`${API_BASE}/api/simulator/properties/${encodeURIComponent(email)}`);
    if (response.ok) {
      const data = await response.json();
      return data.properties || [];
    }
  } catch (e) {
    console.error('Error loading from API:', e);
  }
  return [];
};

const deleteFromAPI = async (email, propertyName) => {
  try {
    const response = await fetch(
      `${API_BASE}/api/simulator/property/${encodeURIComponent(email)}/${encodeURIComponent(propertyName)}`,
      { method: 'DELETE' }
    );
    return response.ok;
  } catch (e) {
    console.error('Error deleting from API:', e);
    return false;
  }
};

const loadAllPropertiesAdmin = async (email, password) => {
  try {
    const response = await fetch(
      `${API_BASE}/api/simulator/admin/all-properties?admin_email=${encodeURIComponent(email)}&admin_password=${encodeURIComponent(password)}`
    );
    if (response.ok) {
      return await response.json();
    }
  } catch (e) {
    console.error('Error loading admin data:', e);
  }
  return null;
};

// Tooltip component
const Tooltip = ({ text }) => (
  <div className="group relative inline-block ml-1">
    <HelpCircle size={14} className="text-stone-400 hover:text-amber-600 cursor-help" />
    <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-2 bg-stone-800 text-white text-xs rounded-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 w-48 z-50">
      {text}
      <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-stone-800" />
    </div>
  </div>
);

// Collapsible Section component
const CollapsibleSection = ({ title, children, defaultOpen = true }) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  return (
    <div className="border border-stone-200 rounded-xl overflow-hidden mb-4">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between p-4 bg-stone-50 hover:bg-stone-100 transition-colors"
      >
        <span className="font-medium text-stone-800">{title}</span>
        {isOpen ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
      </button>
      {isOpen && <div className="p-4 bg-white">{children}</div>}
    </div>
  );
};

// Number Input component
const NumberInput = ({ label, value, onChange, min = 0, step = 1000, tooltip, suffix = '円' }) => (
  <div className="space-y-1">
    <label className="flex items-center text-sm text-stone-700">
      {label}
      {tooltip && <Tooltip text={tooltip} />}
    </label>
    <div className="relative">
      <input
        type="number"
        value={value}
        onChange={(e) => onChange(parseFloat(e.target.value) || 0)}
        min={min}
        step={step}
        className="w-full px-3 py-2 border border-stone-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-400/50 focus:border-amber-400 text-right pr-10"
      />
      <span className="absolute right-3 top-1/2 -translate-y-1/2 text-stone-400 text-sm">{suffix}</span>
    </div>
  </div>
);

// Login Screen
const LoginScreen = ({ onLogin, initialEmail }) => {
  const [email, setEmail] = useState(initialEmail || '');
  const [passcode, setPasscode] = useState('');
  const [error, setError] = useState('');

  const handleSubmit = (e) => {
    e.preventDefault();
    const emailLower = email.toLowerCase().trim();
    
    if (!email.includes('@')) {
      setError('有効なメールアドレスを入力してください。');
      return;
    }
    
    // Check for admin account first
    if (emailLower === ADMIN_ACCOUNT.email && passcode === ADMIN_ACCOUNT.password) {
      onLogin(emailLower, passcode);
      return;
    }
    
    // Regular user login with master passcode
    if (passcode !== MASTER_CODE) {
      setError('パスコードが正しくありません。');
      return;
    }
    
    onLogin(emailLower, passcode);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-stone-50 to-amber-50/30 flex items-center justify-center p-6">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="flex items-center justify-center gap-2 mb-4">
            <Building2 size={32} className="text-amber-600" />
            <h1 className="text-2xl font-light text-stone-800">物件収支シミュレーター</h1>
          </div>
          <p className="text-stone-500 text-sm">ACOE Property Simulator</p>
        </div>
        
        <form onSubmit={handleSubmit} className="bg-white rounded-2xl shadow-lg border border-stone-100 p-8">
          <div className="space-y-4">
            <div>
              <label className="block text-sm text-stone-700 mb-2">メールアドレス</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full px-4 py-3 border border-stone-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-amber-400/50 focus:border-amber-400"
                placeholder="example@email.com"
              />
            </div>
            <div>
              <label className="block text-sm text-stone-700 mb-2">パスコード</label>
              <input
                type="password"
                value={passcode}
                onChange={(e) => setPasscode(e.target.value)}
                className="w-full px-4 py-3 border border-stone-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-amber-400/50 focus:border-amber-400"
                placeholder="••••••••••"
              />
            </div>
            {error && (
              <p className="text-red-500 text-sm bg-red-50 p-3 rounded-lg">{error}</p>
            )}
            <button
              type="submit"
              className="w-full bg-stone-800 hover:bg-stone-700 text-white py-3 rounded-xl font-medium transition-colors"
            >
              ログイン
            </button>
          </div>
        </form>
        
        <p className="text-center text-stone-400 text-xs mt-6">
          © ACOE - Lifestyle Accommodation Brand
        </p>
      </div>
    </div>
  );
};

// Main Simulator Component
const PropertySimulator = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const initialEmail = searchParams.get('email') || '';
  
  const [userEmail, setUserEmail] = useState(null);
  const [userPassword, setUserPassword] = useState(null); // Store password for admin functions
  const [savedProperties, setSavedProperties] = useState([]);
  const [selectedProperty, setSelectedProperty] = useState('');
  const [newPropertyName, setNewPropertyName] = useState('');
  const [saveMessage, setSaveMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showAdminPanel, setShowAdminPanel] = useState(false);
  const [adminData, setAdminData] = useState(null);
  const [loadedPropertyName, setLoadedPropertyName] = useState(''); // Track currently loaded property
  
  // Check if current user is admin
  const isAdmin = userEmail === ADMIN_ACCOUNT.email;
  
  // Form state - Initial costs
  const [rentTotal, setRentTotal] = useState(0);
  const [prepMonths, setPrepMonths] = useState(2);
  const [shikikin, setShikikin] = useState(0);
  const [reikin, setReikin] = useState(0);
  const [brokerFee, setBrokerFee] = useState(0);
  const [guarFee, setGuarFee] = useState(0);
  const [renovation, setRenovation] = useState(0);
  const [furniture, setFurniture] = useState(0);
  const [licenseFee, setLicenseFee] = useState(0);
  const [fireWork, setFireWork] = useState(0);
  const [otherInit, setOtherInit] = useState(0);
  
  // Room configurations
  const [numRoomTypes, setNumRoomTypes] = useState(1);
  const [roomConfigs, setRoomConfigs] = useState([
    { name: 'タイプ A', count: 1, adr: 0, cons: 0, util: 0 }
  ]);
  
  // Operating costs
  const [occupancyRate, setOccupancyRate] = useState(70);
  const [otaFee, setOtaFee] = useState(15);
  const [mgmtFee, setMgmtFee] = useState(20);
  const [fixedCosts, setFixedCosts] = useState(0);
  const [capexRate, setCapexRate] = useState(3);
  
  // Target profit
  const [targetProfit, setTargetProfit] = useState(500000);

  // Load saved properties when user logs in
  useEffect(() => {
    if (userEmail) {
      setIsLoading(true);
      loadFromAPI(userEmail).then(properties => {
        setSavedProperties(properties);
        setIsLoading(false);
      });
    }
  }, [userEmail]);

  // Update room configs when number of types changes
  useEffect(() => {
    setRoomConfigs(prev => {
      const newConfigs = [...prev];
      while (newConfigs.length < numRoomTypes) {
        newConfigs.push({ 
          name: `タイプ ${String.fromCharCode(65 + newConfigs.length)}`, 
          count: 1, adr: 0, cons: 0, util: 0 
        });
      }
      return newConfigs.slice(0, numRoomTypes);
    });
  }, [numRoomTypes]);

  const updateRoomConfig = (index, field, value) => {
    setRoomConfigs(prev => {
      const newConfigs = [...prev];
      newConfigs[index] = { ...newConfigs[index], [field]: value };
      return newConfigs;
    });
  };

  // Calculations
  const days = 30;
  const occRate = occupancyRate / 100;
  const prepRentCost = rentTotal * prepMonths;
  
  const totalRevenue = roomConfigs.reduce((sum, r) => 
    sum + (r.adr * r.count * days * occRate), 0);
  
  const totalVarCosts = roomConfigs.reduce((sum, r) => 
    sum + ((r.cons + r.util) * r.count * days * occRate), 0);
  
  const feeRate = (otaFee + mgmtFee + capexRate) / 100;
  const commissions = totalRevenue * feeRate;
  
  const monthlyExpenses = rentTotal + totalVarCosts + fixedCosts + commissions;
  const profit = totalRevenue - monthlyExpenses;
  
  const startupTotal = prepRentCost + shikikin + reikin + brokerFee + guarFee + 
    renovation + furniture + licenseFee + fireWork + otherInit;
  
  const paybackMonths = profit > 0 ? startupTotal / profit : 0;

  // Strategy calculations
  const totalRooms = roomConfigs.reduce((sum, r) => sum + r.count, 0);
  const fixedTotal = rentTotal + fixedCosts;
  const feeDenom = 1 - feeRate;
  const avgAdr = totalRooms > 0 ? roomConfigs.reduce((sum, r) => sum + r.adr * r.count, 0) / totalRooms : 0;
  const avgVarNight = totalRooms > 0 ? roomConfigs.reduce((sum, r) => sum + (r.cons + r.util) * r.count, 0) / totalRooms : 0;

  // Handlers
  const handleLogin = (email, password) => {
    setUserEmail(email);
    setUserPassword(password);
  };

  const handleLogout = () => {
    setUserEmail(null);
    setUserPassword(null);
    setShowAdminPanel(false);
    setAdminData(null);
    navigate('/simulator');
  };

  const handleSave = async () => {
    if (!newPropertyName.trim()) {
      setSaveMessage('物件名を入力してください。');
      return;
    }
    setIsLoading(true);
    const formData = {
      rentTotal, prepMonths, shikikin, reikin, brokerFee, guarFee,
      renovation, furniture, licenseFee, fireWork, otherInit,
      numRoomTypes, roomConfigs, occupancyRate, otaFee, mgmtFee,
      fixedCosts, capexRate, targetProfit
    };
    const success = await saveToAPI(userEmail, newPropertyName.trim(), formData);
    if (success) {
      const properties = await loadFromAPI(userEmail);
      setSavedProperties(properties);
      setSaveMessage('保存しました！');
    } else {
      setSaveMessage('保存に失敗しました。');
    }
    setIsLoading(false);
    setTimeout(() => setSaveMessage(''), 3000);
  };

  const handleLoad = () => {
    if (!selectedProperty) return;
    const prop = savedProperties.find(p => p.property_name === selectedProperty);
    if (prop && prop.data) {
      const data = prop.data;
      setRentTotal(data.rentTotal ?? 0);
      setPrepMonths(data.prepMonths ?? 2);
      setShikikin(data.shikikin ?? 0);
      setReikin(data.reikin ?? 0);
      setBrokerFee(data.brokerFee ?? 0);
      setGuarFee(data.guarFee ?? 0);
      setRenovation(data.renovation ?? 0);
      setFurniture(data.furniture ?? 0);
      setLicenseFee(data.licenseFee ?? 0);
      setFireWork(data.fireWork ?? 0);
      setOtherInit(data.otherInit ?? 0);
      setNumRoomTypes(data.numRoomTypes ?? 1);
      setRoomConfigs(data.roomConfigs ?? [{ name: 'タイプ A', count: 1, adr: 0, cons: 0, util: 0 }]);
      setOccupancyRate(data.occupancyRate ?? 70);
      setOtaFee(data.otaFee ?? 15);
      setMgmtFee(data.mgmtFee ?? 20);
      setFixedCosts(data.fixedCosts ?? 0);
      setCapexRate(data.capexRate ?? 3);
      setTargetProfit(data.targetProfit ?? 500000);
      setLoadedPropertyName(selectedProperty); // Track loaded property
      setSaveMessage('読み込みました！');
      setTimeout(() => setSaveMessage(''), 3000);
    }
  };

  // Update existing property
  const handleUpdate = async () => {
    if (!loadedPropertyName) return;
    setIsLoading(true);
    const formData = {
      rentTotal, prepMonths, shikikin, reikin, brokerFee, guarFee,
      renovation, furniture, licenseFee, fireWork, otherInit,
      numRoomTypes, roomConfigs, occupancyRate, otaFee, mgmtFee,
      fixedCosts, capexRate, targetProfit
    };
    const success = await saveToAPI(userEmail, loadedPropertyName, formData);
    if (success) {
      const properties = await loadFromAPI(userEmail);
      setSavedProperties(properties);
      setSaveMessage(`「${loadedPropertyName}」を更新しました！`);
    } else {
      setSaveMessage('更新に失敗しました。');
    }
    setIsLoading(false);
    setTimeout(() => setSaveMessage(''), 3000);
  };

  const handleDelete = async () => {
    if (!selectedProperty) return;
    setIsLoading(true);
    const success = await deleteFromAPI(userEmail, selectedProperty);
    if (success) {
      const properties = await loadFromAPI(userEmail);
      setSavedProperties(properties);
      setSelectedProperty('');
      // Clear loaded property if it was deleted
      if (selectedProperty === loadedPropertyName) {
        setLoadedPropertyName('');
      }
      setSaveMessage('削除しました。');
    } else {
      setSaveMessage('削除に失敗しました。');
    }
    setIsLoading(false);
    setTimeout(() => setSaveMessage(''), 3000);
  };

  const handleLoadAdminData = async () => {
    setIsLoading(true);
    const data = await loadAllPropertiesAdmin(userEmail, userPassword);
    if (data) {
      setAdminData(data);
      setShowAdminPanel(true);
    } else {
      setSaveMessage('管理者データの取得に失敗しました。');
      setTimeout(() => setSaveMessage(''), 3000);
    }
    setIsLoading(false);
  };

  if (!userEmail) {
    return <LoginScreen onLogin={handleLogin} initialEmail={initialEmail} />;
  }

  // Pie chart data
  const startupData = [
    { name: '空家賃', value: prepRentCost, color: '#f59e0b' },
    { name: '敷金', value: shikikin, color: '#84cc16' },
    { name: '礼金', value: reikin, color: '#22c55e' },
    { name: '仲介料', value: brokerFee, color: '#14b8a6' },
    { name: '保険', value: guarFee, color: '#06b6d4' },
    { name: 'リフォーム', value: renovation, color: '#3b82f6' },
    { name: '家具家電', value: furniture, color: '#8b5cf6' },
    { name: '許可', value: licenseFee, color: '#d946ef' },
    { name: '消防工事', value: fireWork, color: '#ec4899' },
    { name: 'その他', value: otherInit, color: '#f43f5e' },
  ].filter(d => d.value > 0);

  const monthlyData = [
    { name: '家賃', value: rentTotal, color: '#f59e0b' },
    { name: '変動費', value: totalVarCosts, color: '#3b82f6' },
    { name: '固定運営費', value: fixedCosts, color: '#8b5cf6' },
    { name: '各種手数料', value: commissions, color: '#ec4899' },
    { name: '利益', value: Math.max(0, profit), color: '#22c55e' },
  ].filter(d => d.value > 0);

  // Simple pie chart renderer
  // Function to load a specific property's data (used by admin panel)
  const loadPropertyData = (data) => {
    if (data) {
      setRentTotal(data.rentTotal ?? 0);
      setPrepMonths(data.prepMonths ?? 2);
      setShikikin(data.shikikin ?? 0);
      setReikin(data.reikin ?? 0);
      setBrokerFee(data.brokerFee ?? 0);
      setGuarFee(data.guarFee ?? 0);
      setRenovation(data.renovation ?? 0);
      setFurniture(data.furniture ?? 0);
      setLicenseFee(data.licenseFee ?? 0);
      setFireWork(data.fireWork ?? 0);
      setOtherInit(data.otherInit ?? 0);
      setNumRoomTypes(data.numRoomTypes ?? 1);
      setRoomConfigs(data.roomConfigs ?? [{ name: 'タイプ A', count: 1, adr: 0, cons: 0, util: 0 }]);
      setOccupancyRate(data.occupancyRate ?? 70);
      setOtaFee(data.otaFee ?? 15);
      setMgmtFee(data.mgmtFee ?? 20);
      setFixedCosts(data.fixedCosts ?? 0);
      setCapexRate(data.capexRate ?? 3);
      setTargetProfit(data.targetProfit ?? 500000);
    }
  };

  // Handle clicking a property in admin panel
  const handleAdminPropertyClick = (prop) => {
    loadPropertyData(prop.data);
    setNewPropertyName(prop.property_name);
    setLoadedPropertyName(prop.property_name); // Track loaded property for updates
    setShowAdminPanel(false);
    setSaveMessage(`「${prop.property_name}」を読み込みました`);
    setTimeout(() => setSaveMessage(''), 3000);
  };

  const renderPieChart = (data, total) => {
    let cumulative = 0;
    return (
      <div className="flex flex-col items-center">
        <svg viewBox="0 0 100 100" className="w-48 h-48">
          {data.map((item, i) => {
            const percentage = item.value / total;
            const startAngle = cumulative * 360;
            cumulative += percentage;
            const endAngle = cumulative * 360;
            const largeArc = percentage > 0.5 ? 1 : 0;
            const startRad = (startAngle - 90) * Math.PI / 180;
            const endRad = (endAngle - 90) * Math.PI / 180;
            const x1 = 50 + 40 * Math.cos(startRad);
            const y1 = 50 + 40 * Math.sin(startRad);
            const x2 = 50 + 40 * Math.cos(endRad);
            const y2 = 50 + 40 * Math.sin(endRad);
            return (
              <path
                key={i}
                d={`M 50 50 L ${x1} ${y1} A 40 40 0 ${largeArc} 1 ${x2} ${y2} Z`}
                fill={item.color}
                className="hover:opacity-80 transition-opacity cursor-pointer"
              />
            );
          })}
          <circle cx="50" cy="50" r="20" fill="white" />
        </svg>
        <div className="grid grid-cols-2 gap-x-4 gap-y-1 mt-4 text-xs">
          {data.map((item, i) => {
            const pct = ((item.value / total) * 100).toFixed(1);
            return (
              <div key={i} className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: item.color }} />
                <span className="text-stone-600">{item.name}</span>
                <span className="text-stone-400 ml-auto">{pct}%</span>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-stone-50 to-amber-50/30">
      {/* Header */}
      <header className="bg-white border-b border-stone-200 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Building2 size={28} className="text-amber-600" />
            <div>
              <h1 className="text-lg font-medium text-stone-800">物件収支シミュレーター</h1>
              <p className="text-xs text-stone-400">ACOE Property Simulator</p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <span className="text-sm text-stone-500">{userEmail}</span>
            <button
              onClick={handleLogout}
              className="flex items-center gap-2 text-stone-500 hover:text-stone-700 text-sm"
            >
              <LogOut size={16} />
              ログアウト
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 py-6">
        <div className="grid lg:grid-cols-[320px_1fr] gap-6">
          {/* Sidebar - Input Form */}
          <div className="space-y-4">
            {/* Cloud Storage */}
            <div className="bg-white rounded-xl border border-stone-200 p-4">
              <h3 className="font-medium text-stone-800 mb-3 flex items-center gap-2">
                <Download size={16} />
                保存・読込
              </h3>
              {savedProperties.length > 0 && (
                <div className="space-y-2 mb-4">
                  <select
                    value={selectedProperty}
                    onChange={(e) => setSelectedProperty(e.target.value)}
                    className="w-full px-3 py-2 border border-stone-200 rounded-lg text-sm"
                  >
                    <option value="">物件を選択...</option>
                    {savedProperties.map(prop => (
                      <option key={prop.property_name} value={prop.property_name}>
                        {prop.property_name}
                      </option>
                    ))}
                  </select>
                  <div className="grid grid-cols-2 gap-2">
                    <button
                      onClick={handleLoad}
                      disabled={!selectedProperty || isLoading}
                      className="px-3 py-2 bg-stone-100 hover:bg-stone-200 disabled:opacity-50 rounded-lg text-sm transition-colors"
                    >
                      📥 読込
                    </button>
                    <button
                      onClick={handleDelete}
                      disabled={!selectedProperty || isLoading}
                      className="px-3 py-2 bg-red-50 hover:bg-red-100 text-red-600 disabled:opacity-50 rounded-lg text-sm transition-colors"
                    >
                      🗑️ 削除
                    </button>
                  </div>
                </div>
              )}
              <div className="space-y-2">
                <input
                  type="text"
                  value={newPropertyName}
                  onChange={(e) => setNewPropertyName(e.target.value)}
                  placeholder="物件名を入力..."
                  className="w-full px-3 py-2 border border-stone-200 rounded-lg text-sm"
                />
                <button
                  onClick={handleSave}
                  disabled={isLoading}
                  className="w-full px-3 py-2 bg-amber-500 hover:bg-amber-600 disabled:opacity-50 text-white rounded-lg text-sm font-medium transition-colors flex items-center justify-center gap-2"
                >
                  <Save size={14} />
                  {isLoading ? '保存中...' : '新規保存'}
                </button>
                
                {/* Update button - only shows when a property is loaded */}
                {loadedPropertyName && (
                  <button
                    onClick={handleUpdate}
                    disabled={isLoading}
                    className="w-full px-3 py-2 bg-blue-500 hover:bg-blue-600 disabled:opacity-50 text-white rounded-lg text-sm font-medium transition-colors flex items-center justify-center gap-2"
                  >
                    📝 「{loadedPropertyName}」を更新
                  </button>
                )}
              </div>
              {saveMessage && (
                <p className={`mt-2 text-sm p-2 rounded-lg text-center ${saveMessage.includes('失敗') ? 'text-red-600 bg-red-50' : 'text-green-600 bg-green-50'}`}>
                  {saveMessage}
                </p>
              )}
              
              {/* Admin Panel Button */}
              {isAdmin && (
                <div className="mt-4 pt-4 border-t border-stone-200">
                  <button
                    onClick={handleLoadAdminData}
                    disabled={isLoading}
                    className="w-full px-3 py-2 bg-purple-600 hover:bg-purple-700 disabled:opacity-50 text-white rounded-lg text-sm font-medium transition-colors"
                  >
                    🔐 全ユーザーデータを表示
                  </button>
                </div>
              )}
            </div>

            {/* 1. Initial Costs */}
            <CollapsibleSection title="1. 初期費用・準備期間設定">
              <div className="space-y-3">
                <NumberInput
                  label="月額ローン及び家賃"
                  value={rentTotal}
                  onChange={setRentTotal}
                  tooltip="物件の毎月の家賃またはローン返済額です。"
                />
                <NumberInput
                  label="開業準備期間"
                  value={prepMonths}
                  onChange={setPrepMonths}
                  min={0}
                  step={1}
                  suffix="ヶ月"
                  tooltip="家具の設置や工事など、売上が発生せず家賃のみが発生する期間です。"
                />
                <div className="h-px bg-stone-200 my-2" />
                <NumberInput label="敷金" value={shikikin} onChange={setShikikin} tooltip="契約時に支払う敷金の総額です。" />
                <NumberInput label="礼金" value={reikin} onChange={setReikin} tooltip="契約時に支払う礼金の総額です。" />
                <NumberInput label="仲介手数料" value={brokerFee} onChange={setBrokerFee} tooltip="不動産会社に支払う仲介手数料です。" />
                <NumberInput label="保険会社費用" value={guarFee} onChange={setGuarFee} tooltip="家賃保証会社や火災保険料の総額です。" />
                <NumberInput label="リフォーム費用" value={renovation} onChange={setRenovation} step={10000} tooltip="改装や修繕にかかる費用です。" />
                <NumberInput label="家具・家電費用" value={furniture} onChange={setFurniture} step={10000} tooltip="ベッド、家電、備品の購入費用です。" />
                <NumberInput label="旅館業許可申請" value={licenseFee} onChange={setLicenseFee} tooltip="行政書士への依頼費用や申請手数料です。" />
                <NumberInput label="消防設備工事" value={fireWork} onChange={setFireWork} tooltip="誘導灯や火災報知器の設置工事費用です。" />
                <NumberInput label="その他予備費" value={otherInit} onChange={setOtherInit} tooltip="写真撮影やその他諸経費です。" />
              </div>
            </CollapsibleSection>

            {/* 2. Room Types */}
            <CollapsibleSection title="2. 部屋別の設定">
              <div className="space-y-3">
                <NumberInput
                  label="部屋タイプの種類数"
                  value={numRoomTypes}
                  onChange={(v) => setNumRoomTypes(Math.min(5, Math.max(1, v)))}
                  min={1}
                  step={1}
                  suffix="種類"
                  tooltip="広さや料金が異なる部屋のバリエーション数です。"
                />
                {roomConfigs.map((room, i) => (
                  <div key={i} className="bg-stone-50 p-3 rounded-lg space-y-2">
                    <p className="text-sm font-medium text-stone-700">部屋タイプ {i + 1}</p>
                    <NumberInput
                      label="部屋数"
                      value={room.count}
                      onChange={(v) => updateRoomConfig(i, 'count', v)}
                      min={1}
                      step={1}
                      suffix="室"
                      tooltip="このタイプの部屋がいくつあるか。"
                    />
                    <NumberInput
                      label="ADR (平均客室単価)"
                      value={room.adr}
                      onChange={(v) => updateRoomConfig(i, 'adr', v)}
                      step={500}
                      tooltip="1泊あたりの平均客室単価です。"
                    />
                    <NumberInput
                      label="消耗品/泊"
                      value={room.cons}
                      onChange={(v) => updateRoomConfig(i, 'cons', v)}
                      step={10}
                      tooltip="1泊あたりにかかるアメニティ等の実費です。"
                    />
                    <NumberInput
                      label="光熱費/泊"
                      value={room.util}
                      onChange={(v) => updateRoomConfig(i, 'util', v)}
                      step={10}
                      tooltip="1泊あたりの平均的な電気・水道代です。"
                    />
                  </div>
                ))}
              </div>
            </CollapsibleSection>

            {/* 3. Operating Costs */}
            <CollapsibleSection title="3. 運営コスト・稼働率設定">
              <div className="space-y-3">
                <NumberInput
                  label="想定稼働率"
                  value={occupancyRate}
                  onChange={setOccupancyRate}
                  min={0}
                  step={1}
                  suffix="%"
                  tooltip="1ヶ月の中で部屋が埋まる割合です。"
                />
                <NumberInput
                  label="OTA手数料"
                  value={otaFee}
                  onChange={setOtaFee}
                  min={0}
                  step={0.1}
                  suffix="%"
                  tooltip="予約サイト（Airbnb等）に支払う手数料率です。"
                />
                <NumberInput
                  label="運営管理費"
                  value={mgmtFee}
                  onChange={setMgmtFee}
                  min={0}
                  step={0.5}
                  suffix="%"
                  tooltip="管理会社に支払う委託料率です。"
                />
                <NumberInput
                  label="固定運営費"
                  value={fixedCosts}
                  onChange={setFixedCosts}
                  tooltip="ネット代やシステム利用料など毎月固定の出費です。"
                />
                <NumberInput
                  label="修繕積立 (CAPEX)"
                  value={capexRate}
                  onChange={setCapexRate}
                  min={0}
                  step={0.5}
                  suffix="%"
                  tooltip="将来の修繕や備品交換のために売上から積み立てる割合です。"
                />
              </div>
            </CollapsibleSection>

            {/* 4. Target Profit */}
            <CollapsibleSection title="4. 目標利益の設定">
              <NumberInput
                label="目標月間利益"
                value={targetProfit}
                onChange={setTargetProfit}
                step={10000}
                tooltip="毎月手元に残したい理想の利益額です。"
              />
            </CollapsibleSection>
          </div>

          {/* Main Content - Results */}
          <div className="space-y-6">
            {/* Summary Metrics */}
            <div className="bg-white rounded-xl border border-stone-200 p-6">
              <h2 className="text-lg font-medium text-stone-800 mb-4 flex items-center gap-2">
                <Calculator size={20} className="text-amber-600" />
                収支シミュレーション結果
              </h2>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                <div className="bg-stone-50 rounded-xl p-4">
                  <p className="text-xs text-stone-500 mb-1">初期投資合計</p>
                  <p className="text-lg font-semibold text-stone-800">{fmt(startupTotal)}</p>
                </div>
                <div className="bg-blue-50 rounded-xl p-4">
                  <p className="text-xs text-blue-600 mb-1">月間想定売上</p>
                  <p className="text-lg font-semibold text-blue-700">{fmt(totalRevenue)}</p>
                </div>
                <div className="bg-amber-50 rounded-xl p-4">
                  <p className="text-xs text-amber-600 mb-1">月間費用合計</p>
                  <p className="text-lg font-semibold text-amber-700">{fmt(monthlyExpenses)}</p>
                </div>
                <div className={`rounded-xl p-4 ${profit >= 0 ? 'bg-green-50' : 'bg-red-50'}`}>
                  <p className={`text-xs mb-1 ${profit >= 0 ? 'text-green-600' : 'text-red-600'}`}>月間営業利益</p>
                  <p className={`text-lg font-semibold ${profit >= 0 ? 'text-green-700' : 'text-red-700'}`}>{fmt(profit)}</p>
                </div>
                <div className="bg-purple-50 rounded-xl p-4">
                  <p className="text-xs text-purple-600 mb-1">投資回収期間</p>
                  <p className="text-lg font-semibold text-purple-700">
                    {profit > 0 ? `${paybackMonths.toFixed(1)} ヶ月` : '回収不可'}
                  </p>
                </div>
              </div>
            </div>

            {/* Charts */}
            <div className="grid md:grid-cols-2 gap-6">
              <div className="bg-white rounded-xl border border-stone-200 p-6">
                <h3 className="text-base font-medium text-stone-800 mb-4 flex items-center gap-2">
                  <PieChart size={18} className="text-amber-600" />
                  初期投資の内訳
                </h3>
                {startupTotal > 0 ? renderPieChart(startupData, startupTotal) : (
                  <p className="text-stone-400 text-center py-8">データを入力してください</p>
                )}
              </div>
              <div className="bg-white rounded-xl border border-stone-200 p-6">
                <h3 className="text-base font-medium text-stone-800 mb-4 flex items-center gap-2">
                  <PieChart size={18} className="text-blue-600" />
                  月間費用の詳細
                </h3>
                {(monthlyExpenses + Math.max(0, profit)) > 0 ? renderPieChart(monthlyData, monthlyExpenses + Math.max(0, profit)) : (
                  <p className="text-stone-400 text-center py-8">データを入力してください</p>
                )}
              </div>
            </div>

            {/* Strategy Analysis */}
            <div className="bg-white rounded-xl border border-stone-200 p-6">
              <h2 className="text-lg font-medium text-stone-800 mb-4 flex items-center gap-2">
                <TrendingUp size={20} className="text-green-600" />
                戦略分析
              </h2>

              {/* Break-even Analysis */}
              <div className="mb-8">
                <h3 className="text-base font-medium text-stone-700 mb-4">A. 損益分岐点と目標単価の分析</h3>
                <div className="grid md:grid-cols-[1fr_2fr] gap-6">
                  <div className="space-y-3">
                    <div className="bg-blue-50 rounded-lg p-4">
                      <p className="text-xs text-blue-600 mb-1">月間固定費合計</p>
                      <p className="text-lg font-semibold text-blue-700">{fmt(fixedTotal)}</p>
                    </div>
                    <div className="bg-amber-50 rounded-lg p-4">
                      <p className="text-xs text-amber-600 mb-1">目標利益額</p>
                      <p className="text-lg font-semibold text-amber-700">{fmt(targetProfit)}</p>
                    </div>
                    <div className="bg-green-50 rounded-lg p-4">
                      <p className="text-xs text-green-600 mb-1">貢献利益率</p>
                      <p className="text-lg font-semibold text-green-700">{Math.round((1 - feeRate) * 100)}%</p>
                    </div>
                  </div>
                  <div>
                    <p className="text-sm text-stone-600 mb-3">稼働率別の必要ADR（客室単価）</p>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="bg-stone-100">
                            <th className="px-3 py-2 text-left">稼働率</th>
                            <th className="px-3 py-2 text-right">損益分岐ADR</th>
                            <th className="px-3 py-2 text-right">目標達成ADR</th>
                          </tr>
                        </thead>
                        <tbody>
                          {[10, 20, 30, 40, 50, 60, 70, 80, 90, 100].map(occ => {
                            const nights = totalRooms * 30 * (occ / 100);
                            if (nights <= 0 || feeDenom <= 0) return null;
                            const beAdr = (fixedTotal / nights + avgVarNight) / feeDenom;
                            const tgAdr = ((fixedTotal + targetProfit) / nights + avgVarNight) / feeDenom;
                            return (
                              <tr key={occ} className="border-b border-stone-100 hover:bg-stone-50">
                                <td className="px-3 py-2">{occ}%</td>
                                <td className="px-3 py-2 text-right">{fmt(beAdr)}</td>
                                <td className="px-3 py-2 text-right font-medium text-amber-700">{fmt(tgAdr)}</td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </div>

              {/* Sensitivity Analysis */}
              <div>
                <h3 className="text-base font-medium text-stone-700 mb-2">B. 収支感度分析（詳細シミュレーション）</h3>
                <p className="text-xs text-stone-500 mb-4">
                  ※現在の販売単価（ADR）を維持した状態で、稼働率が変化した場合の利益推移です。
                </p>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-stone-100">
                        <th className="px-3 py-2 text-left">稼働率</th>
                        <th className="px-3 py-2 text-right">想定総売上</th>
                        <th className="px-3 py-2 text-right">総支出額</th>
                        <th className="px-3 py-2 text-right">月間利益</th>
                        <th className="px-3 py-2 text-right">利益率</th>
                        <th className="px-3 py-2 text-right">投資回収</th>
                      </tr>
                    </thead>
                    <tbody>
                      {[10, 20, 30, 40, 50, 60, 70, 80, 90, 100].map(occ => {
                        const oNights = totalRooms * 30 * (occ / 100);
                        const oRev = avgAdr * oNights;
                        const oVar = avgVarNight * oNights;
                        const oFees = oRev * feeRate;
                        const oExp = fixedTotal + oVar + oFees;
                        const oProfit = oRev - oExp;
                        const oMargin = oRev > 0 ? (oProfit / oRev * 100) : 0;
                        const oPayback = oProfit > 0 ? startupTotal / oProfit : 0;
                        return (
                          <tr key={occ} className={`border-b border-stone-100 hover:bg-stone-50 ${occ === occupancyRate ? 'bg-amber-50' : ''}`}>
                            <td className="px-3 py-2 font-medium">{occ}%</td>
                            <td className="px-3 py-2 text-right">{fmt(oRev)}</td>
                            <td className="px-3 py-2 text-right">{fmt(oExp)}</td>
                            <td className={`px-3 py-2 text-right font-medium ${oProfit >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                              {fmt(oProfit)}
                            </td>
                            <td className="px-3 py-2 text-right">{oMargin.toFixed(1)}%</td>
                            <td className="px-3 py-2 text-right">
                              {oProfit > 0 ? `${oPayback.toFixed(1)}ヶ月` : '回収不可'}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Admin Panel Modal */}
      {showAdminPanel && adminData && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-5xl max-h-[90vh] overflow-hidden">
            <div className="bg-purple-600 text-white p-4 flex items-center justify-between">
              <h2 className="text-lg font-medium">🔐 管理者パネル - 全ユーザーデータ</h2>
              <button
                onClick={() => setShowAdminPanel(false)}
                className="text-white/80 hover:text-white"
              >
                ✕
              </button>
            </div>
            <div className="p-6 overflow-y-auto max-h-[calc(90vh-80px)]">
              <div className="grid grid-cols-2 gap-4 mb-6">
                <div className="bg-purple-50 rounded-xl p-4">
                  <p className="text-sm text-purple-600 mb-1">総ユーザー数</p>
                  <p className="text-2xl font-bold text-purple-700">{adminData.total_users}</p>
                </div>
                <div className="bg-blue-50 rounded-xl p-4">
                  <p className="text-sm text-blue-600 mb-1">総物件数</p>
                  <p className="text-2xl font-bold text-blue-700">{adminData.total_properties}</p>
                </div>
              </div>
              
              {Object.entries(adminData.users || {}).map(([email, properties]) => (
                <div key={email} className="mb-6 border border-stone-200 rounded-xl overflow-hidden">
                  <div className="bg-stone-100 px-4 py-3 flex items-center justify-between">
                    <span className="font-medium text-stone-800">{email}</span>
                    <span className="text-sm text-stone-500">{properties.length}件の物件</span>
                  </div>
                  <div className="divide-y divide-stone-100">
                    {properties.map((prop, idx) => (
                      <div key={idx} className="p-4 hover:bg-stone-50 transition-colors">
                        <div className="flex items-center justify-between mb-2">
                          <button
                            onClick={() => handleAdminPropertyClick(prop)}
                            className="font-medium text-purple-600 hover:text-purple-800 hover:underline text-left"
                          >
                            📂 {prop.property_name}
                          </button>
                          <span className="text-xs text-stone-400">
                            更新: {new Date(prop.updated_at).toLocaleString('ja-JP')}
                          </span>
                        </div>
                        <div className="grid grid-cols-4 gap-2 text-sm">
                          <div className="bg-stone-50 rounded p-2">
                            <p className="text-xs text-stone-500">月額家賃</p>
                            <p className="font-medium">{fmt(prop.data?.rentTotal || 0)}</p>
                          </div>
                          <div className="bg-stone-50 rounded p-2">
                            <p className="text-xs text-stone-500">部屋数</p>
                            <p className="font-medium">
                              {(prop.data?.roomConfigs || []).reduce((sum, r) => sum + (r.count || 0), 0)}室
                            </p>
                          </div>
                          <div className="bg-stone-50 rounded p-2">
                            <p className="text-xs text-stone-500">想定稼働率</p>
                            <p className="font-medium">{prop.data?.occupancyRate || 0}%</p>
                          </div>
                          <div className="bg-stone-50 rounded p-2">
                            <p className="text-xs text-stone-500">目標利益</p>
                            <p className="font-medium">{fmt(prop.data?.targetProfit || 0)}</p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
              
              {adminData.total_users === 0 && (
                <p className="text-center text-stone-400 py-8">まだ保存されたデータがありません。</p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Footer */}
      <footer className="mt-12 py-6 border-t border-stone-200 bg-white">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <p className="text-stone-400 text-sm">
            © ACOE - Lifestyle Accommodation Brand | 物件収支シミュレーター
          </p>
        </div>
      </footer>
    </div>
  );
};

export default PropertySimulator;
